import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Check, Trash2, User, Baby, Camera, Clock } from 'lucide-react';
import { API_URL } from '../utils/api';

// Small lookup so we can render friendly labels
const GOAL_LABELS = {
  lose_weight: 'Lose weight',
  build_muscle: 'Build muscle',
  eat_cleaner: 'Eat cleaner',
  manage_condition: 'Manage condition',
  feel_better: 'Feel better'
};
const MEDICAL_LABELS = {
  liquid: 'Liquid diet',
  pre_surgery: 'Pre-surgery',
  post_surgery: 'Post-surgery',
  glp1: 'GLP-1 medication',
  diabetic: 'Diabetic',
  other: 'Other condition',
  none: null
};

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/profiles`, { withCredentials: true });
      setProfiles(r.data.profiles || []);
      setActiveId(r.data.active_profile_id || null);
    } catch (e) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    if (id === activeId) return;
    if (navigator.vibrate) navigator.vibrate(30);
    try {
      await axios.post(`${API_URL}/profiles/${id}/activate`, {}, { withCredentials: true });
      setActiveId(id);
      toast.success('Switched profile');
    } catch (e) {
      toast.error('Could not switch');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this profile? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/profiles/${id}`, { withCredentials: true });
      toast.success('Profile deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const handleAdded = async () => {
    setShowAdd(false);
    await load();
  };

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text pb-28"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      data-testid="profiles-screen"
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/home')}
            data-testid="back-button"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-black text-qc-text ml-3 tracking-tight">Profiles</h1>
        </div>
        {!showAdd && (
          <button
            onClick={() => setShowAdd(true)}
            data-testid="add-profile-button"
            className="h-10 px-4 rounded-full bg-qc-green text-qc-bg font-extrabold text-sm flex items-center gap-1 tappable"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> Add
          </button>
        )}
      </div>

      <div className="max-w-lg mx-auto px-5">
        {showAdd ? (
          <AddProfileForm onDone={handleAdded} onCancel={() => setShowAdd(false)} token={token} />
        ) : loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-qc-green border-t-transparent mx-auto" />
          </div>
        ) : (
          <>
            <p className="text-sm text-qc-text-dim mb-4">
              One $4.99 subscription covers everyone. Each profile keeps its own goals, diet, and verdicts.
            </p>
            <div className="space-y-3" data-testid="profiles-list">
              {profiles.map((p) => (
                <ProfileRow
                  key={p.id}
                  profile={p}
                  onActivate={() => activate(p.id)}
                  onDelete={() => remove(p.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Bottom nav */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-qc-bg/95 backdrop-blur-lg border-t border-qc-border flex justify-around items-center z-40"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))', paddingTop: '0.5rem' }}
      >
        <button onClick={() => navigate('/scan')} data-testid="nav-scan" className="flex flex-col items-center gap-1 px-6 py-2 text-qc-text-faint">
          <Camera className="w-6 h-6" /><span className="text-[11px] font-bold">Scan</span>
        </button>
        <button onClick={() => navigate('/history')} data-testid="nav-history" className="flex flex-col items-center gap-1 px-6 py-2 text-qc-text-faint">
          <Clock className="w-6 h-6" /><span className="text-[11px] font-bold">History</span>
        </button>
      </div>
    </div>
  );
}

function ProfileRow({ profile, onActivate, onDelete }) {
  const Icon = profile.is_kid ? Baby : User;
  const medical = MEDICAL_LABELS[profile.medical_diet_type];
  return (
    <div
      className={`w-full rounded-2xl p-4 border-2 transition-all ${
        profile.is_active ? 'bg-qc-green/10 border-qc-green' : 'bg-qc-surface border-qc-border'
      }`}
      data-testid={`profile-row-${profile.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${profile.is_kid ? 'bg-qc-amber/15 text-qc-amber' : 'bg-qc-surface2 text-qc-text-dim'}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-extrabold text-qc-text truncate">
              {profile.name || (profile.is_primary ? 'Primary' : 'Profile')}
            </span>
            {profile.is_primary && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-qc-surface2 text-qc-text-dim uppercase tracking-wider">You</span>}
            {profile.is_kid && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-qc-amber/15 text-qc-amber uppercase tracking-wider">Kid</span>}
            {profile.is_active && <span data-testid={`profile-active-${profile.id}`} className="text-[10px] font-black px-1.5 py-0.5 rounded bg-qc-green text-qc-bg uppercase tracking-wider">Active</span>}
          </div>
          <div className="text-xs text-qc-text-dim mt-0.5 truncate">
            {[GOAL_LABELS[profile.goal], medical, profile.strictness ? `${profile.strictness}` : null].filter(Boolean).join(' · ') || 'no preferences set'}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button
          onClick={onActivate}
          disabled={profile.is_active}
          data-testid={`activate-profile-${profile.id}`}
          className="flex-1 h-10 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-xl font-bold text-sm disabled:opacity-30"
        >
          {profile.is_active ? (<><Check className="w-4 h-4 mr-1" strokeWidth={3} /> Active</>) : 'Switch to'}
        </Button>
        {!profile.is_primary && (
          <button
            onClick={onDelete}
            data-testid={`delete-profile-${profile.id}`}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-qc-surface2 border border-qc-border text-qc-red hover:bg-qc-red/10 tappable"
            aria-label="Delete profile"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Two-step add form: 1) name + is_kid + strictness (if kid), 2) medical diet Q + branches
function AddProfileForm({ onDone, onCancel, token }) {
  const [phase, setPhase] = useState('basics'); // basics -> preferences -> medical -> glp1 -> other -> saving
  const [form, setForm] = useState({
    name: '',
    is_kid: false,
    strictness: 'balanced', // parent-picked for kids
    goal: '',
    eating_style: '',
    allergies: [],
    medical_diet_type: '',
    glp1_medication: '',
    glp1_titration_stage: '',
    medical_diet_other_text: ''
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/profiles`, {
        name: form.name.trim(),
        is_kid: form.is_kid,
        strictness: form.strictness,
        goal: form.goal || null,
        eating_style: form.eating_style || null,
        allergies: form.allergies.length ? form.allergies : null,
        medical_diet_type: form.medical_diet_type || null,
        glp1_medication: form.glp1_medication || null,
        glp1_titration_stage: form.glp1_titration_stage || null,
        medical_diet_other_text: form.medical_diet_other_text.trim() || null,
      }, { withCredentials: true });
      toast.success('Profile added');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create profile');
      setSaving(false);
    }
  };

  // Basics phase: name + is_kid (+ kid strictness picker)
  if (phase === 'basics') {
    return (
      <div className="animate-fade-up" data-testid="add-profile-form-basics">
        <h2 className="text-2xl font-black tracking-tight text-qc-text mb-2">Add a profile</h2>
        <p className="text-sm text-qc-text-dim mb-5">One subscription, many profiles. Each keeps its own diet and verdicts.</p>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Name</label>
        <input
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g., Emma, Dad, Mom"
          maxLength={60}
          data-testid="new-profile-name"
          className="w-full h-14 px-4 rounded-xl bg-qc-surface border-2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:outline-none focus:border-qc-green text-base mb-4"
        />

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Is this a kid profile?</label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { v: false, label: 'Adult' },
            { v: true, label: 'Kid' }
          ].map((o) => (
            <button
              key={String(o.v)}
              onClick={() => set('is_kid', o.v)}
              data-testid={`new-profile-iskid-${o.v}`}
              className={`h-12 rounded-xl font-bold text-sm border-2 tappable ${
                form.is_kid === o.v
                  ? 'bg-qc-green/10 border-qc-green text-qc-green'
                  : 'bg-qc-surface border-qc-border text-qc-text-dim'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {form.is_kid && (
          <>
            <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">
              How strict should verdicts be for this kid?
            </label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['strict', 'balanced', 'relaxed'].map((s) => (
                <button
                  key={s}
                  onClick={() => set('strictness', s)}
                  data-testid={`new-profile-strictness-${s}`}
                  className={`h-11 rounded-xl font-bold text-sm capitalize border-2 tappable ${
                    form.strictness === s
                      ? 'bg-qc-green/10 border-qc-green text-qc-green'
                      : 'bg-qc-surface border-qc-border text-qc-text-dim'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-2 mt-2">
          <Button
            onClick={onCancel}
            variant="outline"
            className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold"
            data-testid="new-profile-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!form.name.trim()) { toast.error('Name is required'); return; }
              setPhase('preferences');
            }}
            className="flex-1 h-12 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-xl font-extrabold"
            data-testid="new-profile-next-basics"
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'preferences') {
    return (
      <div className="animate-fade-up" data-testid="add-profile-form-preferences">
        <h2 className="text-2xl font-black tracking-tight text-qc-text mb-2">Their goals</h2>
        <p className="text-sm text-qc-text-dim mb-5">Skip anything you&apos;re not sure about.</p>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Main goal</label>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {Object.entries(GOAL_LABELS).map(([v, l]) => (
            <button
              key={v}
              onClick={() => set('goal', v === form.goal ? '' : v)}
              data-testid={`new-profile-goal-${v}`}
              className={`h-11 rounded-xl font-bold text-xs border-2 tappable px-2 ${
                form.goal === v ? 'bg-qc-green/10 border-qc-green text-qc-green' : 'bg-qc-surface border-qc-border text-qc-text-dim'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Eating style</label>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {['keto', 'vegan', 'vegetarian', 'paleo', 'gluten_free', 'none'].map((v) => (
            <button
              key={v}
              onClick={() => set('eating_style', v === form.eating_style ? '' : v)}
              data-testid={`new-profile-eating-${v}`}
              className={`h-11 rounded-xl font-bold text-xs capitalize border-2 tappable ${
                form.eating_style === v ? 'bg-qc-green/10 border-qc-green text-qc-green' : 'bg-qc-surface border-qc-border text-qc-text-dim'
              }`}
            >
              {v.replace('_', ' ')}
            </button>
          ))}
        </div>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Allergies</label>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {['nuts', 'dairy', 'gluten', 'soy', 'eggs', 'shellfish'].map((v) => {
            const on = form.allergies.includes(v);
            return (
              <button
                key={v}
                onClick={() => set('allergies', on ? form.allergies.filter((x) => x !== v) : [...form.allergies, v])}
                data-testid={`new-profile-allergy-${v}`}
                className={`h-11 rounded-xl font-bold text-xs capitalize border-2 tappable ${
                  on ? 'bg-qc-red/10 border-qc-red text-qc-red' : 'bg-qc-surface border-qc-border text-qc-text-dim'
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-2">
          <Button onClick={() => setPhase('basics')} variant="outline" className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold">
            Back
          </Button>
          <Button onClick={() => setPhase('medical')} className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold" data-testid="new-profile-next-preferences">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'medical') {
    return (
      <div className="animate-fade-up" data-testid="add-profile-form-medical">
        <h2 className="text-2xl font-black tracking-tight text-qc-text mb-2">Medical or doctor-directed diet?</h2>
        <p className="text-sm text-qc-text-dim mb-5">This drives more accurate verdicts.</p>

        <div className="space-y-2 mb-4">
          {[
            { v: 'liquid', l: 'Liquid diet' },
            { v: 'pre_surgery', l: 'Pre-surgery' },
            { v: 'post_surgery', l: 'Post-surgery' },
            { v: 'glp1', l: 'GLP-1 medication' },
            { v: 'diabetic', l: 'Diabetic' },
            { v: 'other', l: 'Other' },
            { v: 'none', l: 'None' }
          ].map((o) => {
            const on = form.medical_diet_type === o.v;
            return (
              <button
                key={o.v}
                onClick={() => set('medical_diet_type', o.v)}
                data-testid={`new-profile-medical-${o.v}`}
                className={`w-full text-left px-4 py-3 rounded-xl font-bold text-sm border-2 tappable ${
                  on ? 'bg-qc-green/10 border-qc-green text-qc-green' : 'bg-qc-surface border-qc-border text-qc-text'
                }`}
              >
                {o.l}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-2">
          <Button onClick={() => setPhase('preferences')} variant="outline" className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold">
            Back
          </Button>
          <Button
            onClick={() => {
              if (!form.medical_diet_type) { toast.error('Pick one (or None)'); return; }
              if (form.medical_diet_type === 'glp1') setPhase('glp1');
              else if (form.medical_diet_type === 'other') setPhase('other');
              else submit();
            }}
            disabled={saving}
            className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold"
            data-testid="new-profile-next-medical"
          >
            {saving ? 'Saving...' : (form.medical_diet_type === 'glp1' || form.medical_diet_type === 'other') ? 'Continue' : 'Save profile'}
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'glp1') {
    return (
      <div className="animate-fade-up" data-testid="add-profile-form-glp1">
        <h2 className="text-2xl font-black tracking-tight text-qc-text mb-2">GLP-1 details</h2>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Medication</label>
        <div className="space-y-2 mb-4">
          {[
            { v: 'semaglutide', l: 'Semaglutide (Ozempic/Wegovy/Rybelsus)' },
            { v: 'tirzepatide', l: 'Tirzepatide (Mounjaro/Zepbound)' },
            { v: 'orforglipron', l: 'Orforglipron (Foundayo)' },
            { v: 'liraglutide', l: 'Liraglutide (Saxenda/Victoza)' },
            { v: 'dulaglutide', l: 'Dulaglutide (Trulicity)' },
            { v: 'not_sure', l: 'Not sure / prefer not to say' }
          ].map((o) => {
            const on = form.glp1_medication === o.v;
            return (
              <button
                key={o.v}
                onClick={() => set('glp1_medication', o.v)}
                data-testid={`new-profile-glp1-med-${o.v}`}
                className={`w-full text-left px-4 py-3 rounded-xl font-bold text-sm border-2 tappable ${
                  on ? 'bg-qc-green/10 border-qc-green text-qc-green' : 'bg-qc-surface border-qc-border text-qc-text'
                }`}
              >
                {o.l}
              </button>
            );
          })}
        </div>

        <label className="block text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-2">Titration stage</label>
        <div className="space-y-2 mb-4">
          {[
            { v: 'starting', l: 'Just started or still increasing my dose' },
            { v: 'maintenance', l: 'On a stable maintenance dose' }
          ].map((o) => {
            const on = form.glp1_titration_stage === o.v;
            return (
              <button
                key={o.v}
                onClick={() => set('glp1_titration_stage', o.v)}
                data-testid={`new-profile-glp1-stage-${o.v}`}
                className={`w-full text-left px-4 py-3 rounded-xl font-bold text-sm border-2 tappable ${
                  on ? 'bg-qc-green/10 border-qc-green text-qc-green' : 'bg-qc-surface border-qc-border text-qc-text'
                }`}
              >
                {o.l}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-2">
          <Button onClick={() => setPhase('medical')} variant="outline" className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold">
            Back
          </Button>
          <Button
            onClick={() => {
              if (!form.glp1_medication || !form.glp1_titration_stage) { toast.error('Pick both'); return; }
              submit();
            }}
            disabled={saving}
            className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold"
            data-testid="new-profile-submit-glp1"
          >
            {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </div>
      </div>
    );
  }

  if (phase === 'other') {
    return (
      <div className="animate-fade-up" data-testid="add-profile-form-other">
        <h2 className="text-2xl font-black tracking-tight text-qc-text mb-2">Tell us about the condition</h2>
        <p className="text-sm text-qc-text-dim mb-4">Verdicts fall back to general tuning until we add specific logic. Always double-check with your care team.</p>
        <textarea
          value={form.medical_diet_other_text}
          onChange={(e) => set('medical_diet_other_text', e.target.value)}
          rows={5}
          maxLength={300}
          data-testid="new-profile-other-text"
          placeholder="e.g., low FODMAP, celiac, EOE, PCOS…"
          className="w-full px-4 py-3 rounded-2xl bg-qc-surface border-2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:outline-none focus:border-qc-green text-base resize-none mb-2"
        />
        <p className="text-xs text-qc-text-faint text-right mb-4">{form.medical_diet_other_text.length} / 300</p>

        <div className="flex gap-2 mt-2">
          <Button onClick={() => setPhase('medical')} variant="outline" className="flex-1 h-12 bg-qc-surface border-qc-border text-qc-text rounded-xl font-bold">
            Back
          </Button>
          <Button
            onClick={() => {
              if (!form.medical_diet_other_text.trim()) { toast.error('Add a short description'); return; }
              submit();
            }}
            disabled={saving}
            className="flex-1 h-12 bg-qc-green text-qc-bg rounded-xl font-extrabold"
            data-testid="new-profile-submit-other"
          >
            {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
